// Reusable "claim this profile" form: pick a handle + secret recovery word so a
// profile can be restored on any device (no email — kid-safe). Used on the
// Profile page and in the post-first-badge "save your racer" nudge. Shows the
// saved state when the profile is already claimed.
import { useState } from "react";
import { claimProfile, recordUsage } from "../lib/api";
import { applyHandle, type LocalProfile } from "../lib/profile";

export default function ClaimProfileForm({
  profile,
  heading = "Save this profile for any device",
  blurb = "Pick a handle and a secret recovery word (no email needed). You'll use them to get your badges back on another device.",
  cta = "🔒 Save my profile",
  onClaimed,
}: {
  profile: LocalProfile;
  heading?: string;
  blurb?: string;
  cta?: string;
  /** Fired with the handle once a claim succeeds. */
  onClaimed?: (handle: string) => void;
}) {
  const [handle, setHandle] = useState("");
  const [passphrase, setPassphrase] = useState("");
  const [claiming, setClaiming] = useState(false);
  const [err, setErr] = useState("");
  const [claimed, setClaimed] = useState<string | null>(profile.handle);

  async function claim() {
    if (!profile.playerToken || claiming) return;
    setClaiming(true);
    setErr("");
    try {
      const res = await claimProfile(profile.playerToken, handle.trim(), passphrase);
      recordUsage("claim:done");
      applyHandle(profile.localId, res.handle);
      setClaimed(res.handle);
      onClaimed?.(res.handle);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Couldn't save that — try again.");
    } finally {
      setClaiming(false);
    }
  }

  if (claimed) {
    return (
      <p className="text-sm font-semibold text-ink/70">
        🔒 Saved as <span className="font-bold text-ink">@{claimed}</span>. Use this
        handle + your recovery word to race as you on any device.
      </p>
    );
  }

  return (
    <>
      <div className="text-sm font-bold uppercase tracking-wide text-ink/70">
        {heading}
      </div>
      <p className="mt-1 text-xs font-medium text-ink/50">{blurb}</p>
      <div className="mt-2 grid gap-2 sm:grid-cols-2">
        <input
          type="text"
          value={handle}
          placeholder="handle"
          autoComplete="off"
          onChange={(e) => setHandle(e.target.value)}
          className="rounded-2xl border-[3px] border-ink bg-white px-3 py-2.5 font-semibold text-ink outline-none placeholder:text-ink/30 focus:border-flame"
        />
        <input
          type="text"
          value={passphrase}
          placeholder="recovery word"
          autoComplete="off"
          onChange={(e) => setPassphrase(e.target.value)}
          className="rounded-2xl border-[3px] border-ink bg-white px-3 py-2.5 font-semibold text-ink outline-none placeholder:text-ink/30 focus:border-flame"
        />
      </div>
      <button
        type="button"
        className="btn-chunk mt-2.5 w-full bg-grass py-2.5 text-white disabled:opacity-50"
        disabled={claiming}
        onClick={claim}
      >
        {claiming ? "Saving…" : cta}
      </button>
      {err && <p className="mt-2 text-xs font-bold text-flame">{err}</p>}
    </>
  );
}
